import React, { useEffect, useReducer } from 'react'
import { useAuth } from '@/context/AuthContext'
import useIntl from '@/utils/intl'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useSearchParams } from 'next/navigation'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import { IMG_URL, resolveImageUrl } from '@/utils/constants'
import Loader from '@/components/ui/loader'
import EMongoliaFail from '../eMongolia/eMongolia_fail'
import { DanResponse } from '@/services/mobi-api/handsetDti/types'
import { useLanguage } from '@/context/LanguageContext'
import { Obj } from '..'
import { useTheme } from '@/context/ThemeContext'

interface Props {
  onClick: (obj: Obj) => void
}

interface State {
  loading: boolean
  errorType?: 'BLACKLIST' | 'UNDERAGE' | 'VERIFICATION_FAILED' | 'REGISTER_MISMATCH_DTI'
  instance?: DanResponse
  id?: string
}

const ConfirmZMS: React.FC<Props> = ({ onClick }) => {
  const { translate } = useIntl()
  const { isWebView } = useAuth()
  const { handset } = useMobiApi()
  const { showError } = useToast()
  const searchParams = useSearchParams()
  const _id = searchParams.get('_id')?.trim()
  const { theme, setTheme } = useTheme()
  const { locale } = useLanguage()
  const [state, setState] = useReducer(
    (state: State, newState: Partial<State>) => ({ ...state, ...newState }),
    {
      loading: true,
      errorType: undefined,
      instance: undefined,
      id: undefined
    }
  )

  const currentObj: Obj = {
    instanceId: state.instance?._id,
    phoneNumber:
      state.instance?.isdn !== undefined && state.instance.isdn !== 'empty'
        ? state.instance.isdn
        : undefined,
    email: state.instance?.email,
    danUrl: state.instance?.danRedirectUrl
  }
  const handleQuery = async (): Promise<void> => {
    setState({ id: _id ?? '' })
    if (_id !== null && _id !== undefined) {
      await fetchRequest(_id)
    }
  }
  useEffect(() => {
    void handleQuery()
  }, [searchParams])

  const fetchRequest = async (_id: string): Promise<void> => {
    try {
      const timeoutPromise = new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), 10000)
      )
      const res = await Promise.race([
        handset.getHandsetDti(_id),
        timeoutPromise
      ])
      if (res !== undefined && (res as DanResponse).error === undefined) {
        const instance = res as DanResponse
        if (!instance.configuratorVerified) {
          const code = instance.configuratorFailedReason?.errorCode
          const errorType =
    code === 'REGISTER_MISMATCH_DTI' ? 'REGISTER_MISMATCH_DTI' : 'VERIFICATION_FAILED'
          setState({
            errorType,
            loading: false
          })

          if (
            instance.theme !== undefined &&
    (instance.theme === 'light' || instance.theme === 'dark')
          ) {
            setTheme(instance.theme)
          }
        } else {
          setState({
            instance,
            errorType: undefined,
            loading: false
          })
        }
      } else {
        const errorRes = res as {
          error?: string
          cause?: { messageEn?: string, messageMn?: string }
        }
        showError(
          locale === 'en'
            ? errorRes?.cause?.messageEn ??
                errorRes?.error ??
                translate('request.failed')
            : errorRes?.cause?.messageMn ??
                errorRes?.error ??
                translate('request.failed')
        )
        setState({ loading: false })
      }
    } catch (e) {
      showError(translate('request.failed'))
      setState({ loading: false })
    }
  }

  const handleButtonClick = (): void => {
    onClick(currentObj)
  }

  const imageSrc = isWebView
    ? `${IMG_URL}/uploads/icons/${
      theme === 'dark' ? 'payment_plan_dark.svg' : 'payment_plan_light.svg'
    }`
    : resolveImageUrl('/uploads/icons/mobot.svg')
  const titleKey = isWebView
    ? 'leasing.confirmzms.title'
    : 'leasing.confirmzms.web.title'
  const descriptionKey = isWebView
    ? 'leasing.confirmzms.description'
    : 'leasing.confirmzms.web.description'
  const buttonKey = isWebView
    ? 'leasing.confirzms.button'
    : 'leasing.confirmzms.button'

  if (state.loading) {
    return (
      <div className='w-full h-96 flex items-center justify-center'>
        <Loader />
      </div>
    )
  }

  if (state.errorType !== undefined) {
    return (
      <EMongoliaFail
        onClick={handleButtonClick}
        danRedirectUrl={state.instance?.danRedirectUrl ?? ''}
        errorType={state.errorType}
      />
    )
  }

  return (
    <div
      className={`bg-surfaceInverse rounded-xl md:shadow w-full md:w-[40vh] h-full md:h-[60vh] flex flex-col ${
        isWebView ? 'min-h-screen justify-center' : 'justify-start'
      } relative no-scrollbar`}
    >
      <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4 mt-40 sm:mt-0'>
        <div className='flex items-center justify-center'>
          <img
            src={imageSrc}
            alt='paymentPlanImageAlt'
            className='w-[200px] h-[200px]'
          />
        </div>
        <span className='text-textHigh font-extrabold text-lg text-center'>
          {translate(titleKey)}
        </span>
        <span className='text-textMid text-sm text-center whitespace-pre-line line-clamp-4'>
          {translate(descriptionKey)}
        </span>
      </div>

      <div className='fixed md:sticky bottom-0 left-0 right-0 rounded-b-xl z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <div className='w-full max-w-md mx-auto flex gap-4'>
          <DarkButton
            fullwidth
            size='sm'
            onClick={handleButtonClick}
            aria-label={translate(buttonKey)}
          >
            <span className='line-clamp-1'>{translate(buttonKey)}</span>
          </DarkButton>
        </div>
      </div>
    </div>
  )
}

export default ConfirmZMS
